/**
 * Organisation Question Templates API service
 *
 * Provides functions for managing reusable question templates at the organisation level.
 */

import { apiClient } from './api'
import type {
  QuestionTemplate,
  CreateQuestionTemplateRequest,
  UpdateQuestionTemplateRequest,
  ListQuestionTemplatesQuery,
  QuestionTemplateCategories,
} from '../types/case-container'

// =============================================================================
// QUESTION TEMPLATE API
// =============================================================================

/**
 * List all question templates for the user's organisation
 */
export async function listQuestionTemplates(
  query?: ListQuestionTemplatesQuery
): Promise<QuestionTemplate[]> {
  const params = new URLSearchParams()
  if (query?.category) params.append('category', query.category)
  if (query?.search) params.append('search', query.search)

  const queryString = params.toString()
  const url = queryString ? `/org/oq-templates?${queryString}` : '/org/oq-templates'
  return apiClient.get<QuestionTemplate[]>(url)
}

/**
 * List available categories
 */
export async function listCategories(): Promise<QuestionTemplateCategories> {
  return apiClient.get<QuestionTemplateCategories>('/org/oq-templates/categories')
}

/**
 * Get a specific template
 */
export async function getQuestionTemplate(id: string): Promise<QuestionTemplate> {
  return apiClient.get<QuestionTemplate>(`/org/oq-templates/${id}`)
}

/**
 * Create a new template
 */
export async function createQuestionTemplate(
  request: CreateQuestionTemplateRequest
): Promise<QuestionTemplate> {
  return apiClient.post<QuestionTemplate>('/org/oq-templates', request)
}

/**
 * Update a template
 */
export async function updateQuestionTemplate(
  id: string,
  request: UpdateQuestionTemplateRequest
): Promise<QuestionTemplate> {
  return apiClient.patch<QuestionTemplate>(`/org/oq-templates/${id}`, request)
}

/**
 * Delete a template
 */
export async function deleteQuestionTemplate(id: string): Promise<void> {
  await apiClient.delete(`/org/oq-templates/${id}`)
}

/**
 * Record that a template was used
 */
export async function recordTemplateUsage(id: string): Promise<void> {
  await apiClient.post(`/org/oq-templates/${id}/use`, {})
}
